<template>
  <v-container fluid class="barcode-page pa-4">
    <v-card outlined class="scanner-card">
      <div class="scanner-header">
        <div>
          <div class="scanner-title">Barcode Scanner Test</div>
          <div class="scanner-subtitle">
            Use your phone camera to scan a product barcode.
          </div>
        </div>

        <v-icon large color="primary">mdi-barcode-scan</v-icon>
      </div>

      <v-divider />

      <v-card-text class="pa-4 pa-sm-6">
        <div v-if="!scannerDialog" class="start-section">
          <div class="camera-icon-wrap">
            <v-icon size="56" color="primary">mdi-camera-outline</v-icon>
          </div>

          <div class="start-title">Ready to scan</div>
          <div class="start-text">
            Tap the button below and allow camera access when your browser asks.
          </div>

          <v-btn
            large
            rounded
            depressed
            color="primary"
            dark
            class="scan-button"
            @click="openScanner"
          >
            <v-icon left>mdi-barcode-scan</v-icon>
            Scan Barcode
          </v-btn>
        </div>

        <v-alert v-if="errorMessage" type="error" outlined class="mt-4 mb-0">
          {{ errorMessage }}
        </v-alert>

        <div v-if="scannedValue" class="result-card mt-5">
          <div class="result-label">Scanned Barcode</div>

          <div class="result-value">
            {{ scannedValue }}
          </div>

          <div v-if="scannedFormat" class="result-format">
            Format: {{ scannedFormat }}
          </div>

          <div class="result-actions">
            <v-btn small rounded outlined color="primary" @click="copyResult">
              <v-icon left small>mdi-content-copy</v-icon>
              Copy
            </v-btn>

            <v-btn small rounded color="primary" dark @click="openScanner">
              <v-icon left small>mdi-barcode-scan</v-icon>
              Scan Again
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="scannerDialog"
      persistent
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card class="scanner-dialog">
        <div class="dialog-header">
          <div>
            <div class="dialog-title">Scan Barcode</div>
            <div class="dialog-subtitle">Place the barcode inside the box.</div>
          </div>

          <v-btn icon dark @click="closeScanner">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="camera-content">
          <div class="camera-box">
            <div id="barcode-reader"></div>
            <div class="scan-guide"></div>
          </div>

          <div class="camera-help">
            Hold the phone steady and keep the barcode well lit.
          </div>

          <v-progress-circular
            v-if="startingCamera"
            indeterminate
            color="white"
            class="mt-5"
          />
        </div>

        <div class="dialog-footer">
          <v-btn
            block
            large
            rounded
            outlined
            dark
            :disabled="startingCamera"
            @click="closeScanner"
          >
            Cancel
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" timeout="2000"> Barcode copied </v-snackbar>
  </v-container>
</template>

<script>
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

export default {
  name: "BarcodeScannerTest",

  data() {
    return {
      scannerDialog: false,
      scanner: null,
      startingCamera: false,
      scannedValue: "",
      scannedFormat: "",
      errorMessage: "",
      snackbar: false,
      scanLocked: false,
    };
  },
  beforeDestroy() {
    this.stopScanner();
    this.unlockOrientation();
  },

  methods: {
    async lockPortraitOrientation() {
      try {
        if (
          screen.orientation &&
          typeof screen.orientation.lock === "function"
        ) {
          await screen.orientation.lock("portrait");
        }
      } catch (error) {
        console.warn("Portrait orientation lock is not supported", error);
      }
    },

    unlockOrientation() {
      try {
        if (
          screen.orientation &&
          typeof screen.orientation.unlock === "function"
        ) {
          screen.orientation.unlock();
        }
      } catch (error) {
        console.warn("Unable to unlock orientation", error);
      }
    },
    async openScanner() {
      this.errorMessage = "";
      this.scanLocked = false;
      this.scannerDialog = true;

      await this.$nextTick();

      await this.lockPortraitOrientation();
      await this.startScanner();
    },

    async startScanner() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.errorMessage =
          "Camera access is not supported by this browser. Try Safari on iPhone or Chrome on Android.";
        this.scannerDialog = false;
        return;
      }

      this.startingCamera = true;

      try {
        if (this.scanner) {
          await this.stopScanner();
        }

        this.scanner = new Html5Qrcode("barcode-reader", {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.CODE_93,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.ITF,
            Html5QrcodeSupportedFormats.CODABAR,
            Html5QrcodeSupportedFormats.QR_CODE,
          ],
          verbose: false,
        });

        const cameraConfig = {
          facingMode: "environment",
        };

        const scanConfig = {
          fps: 10,
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const width = Math.floor(viewfinderWidth * 0.86);
            const height = Math.min(Math.floor(viewfinderHeight * 0.4), 180);

            return { width, height };
          },
          aspectRatio: 1.333334,
          disableFlip: false,
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true,
          },
        };

        await this.scanner.start(
          cameraConfig,
          scanConfig,
          this.onScanSuccess,
          this.onScanFailure
        );
      } catch (error) {
        console.error("Unable to start barcode scanner", error);

        this.errorMessage = this.getCameraErrorMessage(error);
        this.scannerDialog = false;
        await this.stopScanner();
      } finally {
        this.startingCamera = false;
      }
    },

    async onScanSuccess(decodedText, decodedResult) {
      if (this.scanLocked) {
        return;
      }

      this.scanLocked = true;
      this.scannedValue = decodedText;
      this.scannedFormat =
        decodedResult && decodedResult.result
          ? decodedResult.result.format || ""
          : "";

      if (navigator.vibrate) {
        navigator.vibrate(120);
      }

      await this.closeScanner();
    },

    onScanFailure() {
      // Scanning failures happen continuously while no barcode is visible.
      // They should be ignored until a valid barcode is detected.
    },

    async closeScanner() {
      await this.stopScanner();

      this.unlockOrientation();

      this.scannerDialog = false;
      this.startingCamera = false;
    },

    async stopScanner() {
      if (!this.scanner) {
        return;
      }

      try {
        if (this.scanner.isScanning) {
          await this.scanner.stop();
        }

        this.scanner.clear();
      } catch (error) {
        console.warn("Unable to stop barcode scanner", error);
      } finally {
        this.scanner = null;
      }
    },

    getCameraErrorMessage(error) {
      const name = error && error.name ? error.name : "";
      const message = error && error.message ? error.message : "";

      if (name === "NotAllowedError" || /permission/i.test(message)) {
        return "Camera permission was denied. Open your browser settings, allow camera access, then try again.";
      }

      if (name === "NotFoundError" || /not found/i.test(message)) {
        return "No camera was found on this device.";
      }

      if (name === "NotReadableError" || /could not start/i.test(message)) {
        return "The camera is being used by another app. Close the other app and try again.";
      }

      if (!window.isSecureContext) {
        return "Camera access requires HTTPS. Deploy this page using an HTTPS address.";
      }

      return "The camera could not start. Refresh the page and try again.";
    },

    async copyResult() {
      try {
        await navigator.clipboard.writeText(this.scannedValue);
        this.snackbar = true;
      } catch (error) {
        console.warn("Clipboard copy failed", error);
      }
    },
  },
};
</script>

<style scoped>
.barcode-page {
  min-height: 100%;
  background: #f8fafc;
}

.scanner-card {
  max-width: 680px;
  margin: 0 auto;
  border-radius: 22px !important;
  overflow: hidden;
  background: #ffffff;
}

.scanner-header {
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.scanner-title {
  color: #0f172a;
  font-size: 22px;
  font-weight: 800;
}

.scanner-subtitle {
  color: #64748b;
  font-size: 13px;
  margin-top: 3px;
}

.start-section {
  min-height: 390px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.camera-icon-wrap {
  width: 104px;
  height: 104px;
  border-radius: 50%;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.start-title {
  color: #0f172a;
  font-size: 20px;
  font-weight: 700;
}

.start-text {
  max-width: 390px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
  margin-top: 8px;
}

.scan-button {
  min-width: 220px !important;
  margin-top: 24px;
}

.result-card {
  padding: 20px;
  border: 1px solid #bfdbfe;
  border-radius: 16px;
  background: #eff6ff;
  text-align: center;
}

.result-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.result-value {
  color: #0f172a;
  font-family: monospace;
  font-size: 25px;
  font-weight: 800;
  line-height: 1.3;
  overflow-wrap: anywhere;
  margin-top: 10px;
}

.result-format {
  color: #64748b;
  font-size: 13px;
  margin-top: 6px;
}

.result-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 18px;
}

.scanner-dialog {
  min-height: 100vh;
  background: #020617;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  min-height: 74px;
  padding: calc(12px + env(safe-area-inset-top)) 16px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #0f172a;
  color: #ffffff;
  position: sticky;
  top: 0;
  z-index: 10;
}

.dialog-title {
  font-size: 19px;
  font-weight: 700;
}

.dialog-subtitle {
  color: #cbd5e1;
  font-size: 13px;
  margin-top: 2px;
}

.camera-content {
  flex: 1;
  padding: 20px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
}

.camera-box {
  width: 100%;
  max-width: 700px;
  min-height: 300px;
  border-radius: 18px;
  overflow: hidden;
  background: #000000;
  position: relative;
}

#barcode-reader {
  width: 100%;
  min-height: 300px;
}

#barcode-reader::v-deep video {
  width: 100% !important;
  min-height: 300px;
  object-fit: cover;
}

.scan-guide {
  position: absolute;
  left: 8%;
  right: 8%;
  top: 50%;
  height: 150px;
  transform: translateY(-50%);
  border: 3px solid rgba(255, 255, 255, 0.9);
  border-radius: 14px;
  pointer-events: none;
  box-shadow: 0 0 0 9999px rgba(2, 6, 23, 0.3);
}

.scan-guide::after {
  content: "";
  position: absolute;
  left: 5%;
  right: 5%;
  top: 50%;
  height: 2px;
  background: #ef4444;
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.9);
}

.camera-help {
  color: #cbd5e1;
  font-size: 14px;
  text-align: center;
  margin-top: 18px;
}

.dialog-footer {
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: #0f172a;
  position: sticky;
  bottom: 0;
  z-index: 10;
}

@media screen and (orientation: landscape) and (max-height: 600px) {
  .scanner-dialog {
    width: 100vh;
    min-width: 320px;
    max-width: 480px;
    min-height: 100vw;
    margin: 0 auto;
  }

  .camera-content {
    padding: 10px 14px;
  }

  .camera-box {
    max-width: 420px;
    min-height: 230px;
  }

  #barcode-reader,
  #barcode-reader::v-deep video {
    min-height: 230px;
  }

  .scan-guide {
    height: 120px;
  }
}

@media (max-width: 600px) {
  .barcode-page {
    padding: 10px !important;
  }

  .scanner-header {
    padding: 15px 16px;
  }

  .scanner-title {
    font-size: 19px;
  }

  .start-section {
    min-height: 330px;
  }

  .camera-box,
  #barcode-reader,
  #barcode-reader::v-deep video {
    min-height: 260px;
  }
}
</style>
